import { useCallback, useEffect, useRef, useState } from 'react';
import {
    panDomain,
    zoomDomain,
} from '../../markets/components/charts/chartZoom';

export const COMMIT_DELAY_MS = 200;
export const WHEEL_FACTOR = 1.15;

export interface LiveDomain {
    domain: [Date, Date];
    zoomAt: (t: number, factor: number) => void;
    panBy: (deltaMs: number) => void;
}

export function useLiveDomain(
    committed: [Date, Date],
    bounds: [Date, Date],
    onCommit: (next: [Date, Date]) => void
): LiveDomain {
    const [live, setLive] = useState<[Date, Date] | null>(null);
    const domain = live ?? committed;

    const domainRef = useRef(domain);
    domainRef.current = domain;
    const boundsRef = useRef(bounds);
    boundsRef.current = bounds;
    const onCommitRef = useRef(onCommit);
    onCommitRef.current = onCommit;
    const timer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(timer.current), []);

    const apply = useCallback((next: [Date, Date]) => {
        setLive(next);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            onCommitRef.current(next);
            setLive(null);
        }, COMMIT_DELAY_MS);
    }, []);

    const zoomAt = useCallback(
        (t: number, factor: number) =>
            apply(
                zoomDomain(
                    domainRef.current,
                    new Date(t),
                    factor,
                    boundsRef.current
                )
            ),
        [apply]
    );

    const panBy = useCallback(
        (deltaMs: number) =>
            apply(panDomain(domainRef.current, deltaMs, boundsRef.current)),
        [apply]
    );

    return { domain, zoomAt, panBy };
}
